/**
 * 备份涉及的 localStorage 范围：只收集应用自己管理的键，避免把同一 origin 下
 * 其他站点 / 后台的数据带进备份，也避免恢复时误覆盖。
 */
import type { BackupLocalStorage } from './types';
import { profileStorageKey } from '../account/profile';

/** 不区分账号的全局设置键。 */
export const GLOBAL_BACKUP_KEYS = [
  'zxtalk.modelSettings',
  'zxtalk.modelPreference.v1',
  'zxtalk.appearance.selection.v2',
  'zxtalk.appearance.v1',
  'zxtalk.activeConversationId',
  'zxtalk.openaiCompatibilityProfiles.v2',
  'zxtalk.autoMemory.v1',
  'zxtalk.memoryInjection.v1',
  't14.plan',
  't12.searchEnabled',
  't12.searchConfig',
  't10.voiceMode',
] as const;

/** 按账号隔离的键前缀（个人资料 / 语音显示 / API Key）。 */
export const ACCOUNT_BACKUP_PREFIXES = [
  'zxtalk.secret.',
  'zhixin.personal-profile.v1:',
  'zhixin.voiceDisplay.',
] as const;

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** 当前账号允许进入备份 / 允许恢复的键。 */
export function isAllowedBackupStorageKey(key: string, userId: string): boolean {
  if ((GLOBAL_BACKUP_KEYS as readonly string[]).includes(key)) return true;
  if (key.startsWith('zxtalk.secret.')) return true;
  if (key === profileStorageKey(userId)) return true;
  if (key === `zhixin.voiceDisplay.${userId}`) return true;
  if (key.startsWith(`zhixin.voiceDisplay.${userId}.`)) return true;
  return false;
}

/** 收集当前账号的 localStorage 快照（只含白名单键）。 */
export function collectBackupLocalStorage(userId: string): BackupLocalStorage {
  const store = storage();
  const values: BackupLocalStorage = {};
  if (!store) return values;
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key === null || !isAllowedBackupStorageKey(key, userId)) continue;
    const value = store.getItem(key);
    if (value !== null) values[key] = value;
  }
  return values;
}

/** 清空当前账号的备份范围（完整替换前调用）；不碰其他账号的键。 */
export function clearBackupLocalStorage(userId: string): void {
  const store = storage();
  if (!store) return;
  const keys: string[] = [];
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key !== null && isAllowedBackupStorageKey(key, userId)) keys.push(key);
  }
  for (const key of keys) store.removeItem(key);
}

/** 合并 / 替换写入；merge 只覆盖文件里存在的键，replace 先清空再写入。 */
export function applyBackupLocalStorage(
  values: BackupLocalStorage,
  mode: 'merge' | 'replace',
  userId: string,
): void {
  const store = storage();
  if (!store) return;
  if (mode === 'replace') clearBackupLocalStorage(userId);
  for (const [key, value] of Object.entries(values)) {
    if (!isAllowedBackupStorageKey(key, userId)) continue;
    store.setItem(key, value);
  }
}

/** 回滚用：读取整个 localStorage（含非应用键），只用于同一操作内恢复。 */
export function snapshotAllLocalStorage(): BackupLocalStorage {
  const store = storage();
  const values: BackupLocalStorage = {};
  if (!store) return values;
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key === null) continue;
    const value = store.getItem(key);
    if (value !== null) values[key] = value;
  }
  return values;
}

/** 回滚用：把整个 localStorage 精确还原。 */
export function restoreAllLocalStorage(values: BackupLocalStorage): void {
  const store = storage();
  if (!store) return;
  store.clear();
  for (const [key, value] of Object.entries(values)) store.setItem(key, value);
}
